import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { MailOutline as MailIcon } from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage, translateViolations } from '../../utils/apiMessages';

/**
 * "Mot de passe oublié": ask for a link by email.
 *
 * The confirmation is worded the same whether or not the address has an
 * account, because the API answers the same either way — telling a visitor
 * "no account here" would tell anybody which neighbours are on the hub.
 */
function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Entrez votre adresse email');
      return;
    }

    setSending(true);

    try {
      const response = await apiCall('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const violations = translateViolations(data.violations);

        setError(violations.email || translateApiMessage(data.error) || 'La demande n’a pas pu être envoyée.');
        return;
      }

      setSent(true);
    } catch (err) {
      setError('La demande n’a pas pu être envoyée. Réessayez.');
    } finally {
      setSending(false);
    }
  };

  return (
    <PageLayout>
      <Card sx={{ maxWidth: 520, width: '100%', borderRadius: '22px' }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            <Typography variant="h2" component="h1">
              Mot de passe oublié
            </Typography>
            <Typography variant="subtitle1" component="p">
              Indiquez l’adresse email de votre compte : nous vous enverrons un lien pour en
              choisir un nouveau.
            </Typography>
          </Box>

          {sent ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Alert severity="success">
                Si un compte existe pour <strong>{email.trim()}</strong>, un email vient de partir.
                Le lien est valable une heure.
              </Alert>
              <Typography variant="body2">
                Rien reçu après quelques minutes&nbsp;? Regardez dans les indésirables, ou{' '}
                <Link component="button" type="button" onClick={() => setSent(false)} sx={{ verticalAlign: 'baseline' }}>
                  réessayez avec une autre adresse
                </Link>
                .
              </Typography>
              <Button component={RouterLink} to="/login" variant="outlined" size="large" fullWidth>
                Retour à la connexion
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={submit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Adresse électronique"
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                disabled={sending}
                autoComplete="email"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              {error && <Alert severity="error">{error}</Alert>}

              <Button type="submit" variant="contained" size="large" fullWidth disabled={sending}>
                {sending ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1.5, color: 'inherit' }} />
                    Envoi…
                  </>
                ) : (
                  'Recevoir un lien'
                )}
              </Button>

              <Typography variant="body2" align="center">
                <Link component={RouterLink} to="/login">
                  Retour à la connexion
                </Link>
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}

export default ForgotPassword;
